import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { SalesPointDto } from '@ecom/contracts';
import type { AdminLocale } from '@/i18n';
import { compactMoney, count, money, shortDay } from '@/lib/format';
import { cx } from './ui';

/**
 * Revenue per day — the overview's one chart (docs/07-Admin/Dashboard.md).
 *
 * One series, so no legend: the card's title says what is drawn. A 2px line
 * over a 10% wash, hairline gridlines on clean ticks, and a crosshair that
 * snaps to the nearest day and reads out the day's revenue and orders — by
 * pointer, or by the arrow keys once the chart has focus. The same figures sit
 * in a table for a screen reader. Time runs left to right in both languages,
 * like the dates and digits it is read with.
 *
 * Drawn in plain SVG: one chart does not earn a charting library in a bundle
 * a store's staff download on a phone.
 */

const HEIGHT = 220;
const PAD = { top: 12, right: 12, bottom: 26, left: 44 };

export function TrendChart({
  points,
  locale,
  currency = 'SAR',
  label,
  ordersLabel,
  emptyLabel,
  dimmed = false,
}: {
  points: SalesPointDto[];
  locale: AdminLocale;
  currency?: string;
  /** What the chart is, for a screen reader — the card's title. */
  label: string;
  /** The tooltip's word for orders. */
  ordersLabel: string;
  /** Said over a flat line when nothing sold. */
  emptyLabel: string;
  /** Reloading: the last picture stays, faded, rather than flashing a skeleton. */
  dimmed?: boolean;
}) {
  const frame = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(640);
  const [active, setActive] = useState<number | null>(null);
  const tableId = useId();

  useEffect(() => {
    const node = frame.current;
    if (!node) return undefined;
    const observer = new ResizeObserver(([entry]) => setWidth(Math.max(240, entry.contentRect.width)));
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const { ticks, top } = useMemo(() => niceTicks(Math.max(0, ...points.map((point) => point.revenue))), [points]);
  const empty = points.every((point) => point.revenue === 0);

  const plotWidth = width - PAD.left - PAD.right;
  const plotHeight = HEIGHT - PAD.top - PAD.bottom;
  const x = (index: number) => PAD.left + (points.length <= 1 ? plotWidth / 2 : (index / (points.length - 1)) * plotWidth);
  const y = (value: number) => PAD.top + plotHeight - (top === 0 ? 0 : (value / top) * plotHeight);

  const line = points.map((point, index) => `${index === 0 ? 'M' : 'L'}${x(index).toFixed(1)},${y(point.revenue).toFixed(1)}`).join('');
  const area =
    points.length > 0
      ? `${line}L${x(points.length - 1).toFixed(1)},${y(0).toFixed(1)}L${x(0).toFixed(1)},${y(0).toFixed(1)}Z`
      : '';

  // A few day labels — first, last and evenly between — never one per day.
  const dayLabels = points.length === 0 ? [] : [...new Set([0, Math.round((points.length - 1) / 3), Math.round(((points.length - 1) * 2) / 3), points.length - 1])];

  const pick = (clientX: number) => {
    const box = frame.current?.getBoundingClientRect();
    if (!box || points.length === 0) return;
    const relative = clientX - box.left - PAD.left;
    const index = Math.round((relative / plotWidth) * (points.length - 1));
    setActive(Math.min(points.length - 1, Math.max(0, index)));
  };

  const point = active !== null ? points[active] : null;
  // The tooltip sits beside the crosshair, flipped to the other side near the right edge.
  const tooltipLeft = active !== null ? x(active) : 0;
  const flip = tooltipLeft > width - 180;

  return (
    <div
      ref={frame}
      dir="ltr"
      className={cx('relative w-full outline-none transition-opacity', dimmed && 'opacity-50')}
      role="img"
      aria-label={label}
      aria-describedby={tableId}
      tabIndex={0}
      onPointerMove={(event) => pick(event.clientX)}
      onPointerLeave={() => setActive(null)}
      onFocus={() => setActive((current) => current ?? points.length - 1)}
      onBlur={() => setActive(null)}
      onKeyDown={(event) => {
        if (event.key === 'ArrowLeft') setActive((at) => Math.max(0, (at ?? points.length - 1) - 1));
        if (event.key === 'ArrowRight') setActive((at) => Math.min(points.length - 1, (at ?? 0) + 1));
        if (event.key === 'Home') setActive(0);
        if (event.key === 'End') setActive(points.length - 1);
      }}
    >
      <svg width={width} height={HEIGHT} className="block" aria-hidden>
        {ticks.map((tick) => (
          <g key={tick}>
            <line x1={PAD.left} x2={width - PAD.right} y1={y(tick)} y2={y(tick)} className="stroke-line" strokeWidth={1} />
            <text x={PAD.left - 8} y={y(tick)} dy="0.32em" textAnchor="end" className="fill-ink-faint text-[11px]">
              {compactMoney(tick, locale)}
            </text>
          </g>
        ))}
        {dayLabels.map((index) => (
          <text
            key={index}
            x={x(index)}
            y={HEIGHT - 6}
            textAnchor={index === 0 ? 'start' : index === points.length - 1 ? 'end' : 'middle'}
            className="fill-ink-faint text-[11px]"
          >
            {shortDay(points[index].date, locale)}
          </text>
        ))}

        <path d={area} style={{ fill: 'var(--chart-1)' }} fillOpacity={0.1} />
        <path d={line} fill="none" style={{ stroke: 'var(--chart-1)' }} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />

        {/* The latest day marked — and, on a one-day range, the only mark there is. */}
        {points.length > 0 && active === null && !empty ? (
          <g>
            <circle cx={x(points.length - 1)} cy={y(points[points.length - 1].revenue)} r={6} className="fill-surface" />
            <circle
              cx={x(points.length - 1)}
              cy={y(points[points.length - 1].revenue)}
              r={4}
              style={{ fill: 'var(--chart-1)' }}
            />
          </g>
        ) : null}

        {active !== null && point ? (
          <g>
            <line x1={x(active)} x2={x(active)} y1={PAD.top} y2={y(0)} className="stroke-ink-faint" strokeWidth={1} />
            {/* The marker wears a ring in the card's colour so it reads where it crosses the line. */}
            <circle cx={x(active)} cy={y(point.revenue)} r={6} className="fill-surface" />
            <circle cx={x(active)} cy={y(point.revenue)} r={4} style={{ fill: 'var(--chart-1)' }} />
          </g>
        ) : null}
      </svg>

      {empty ? (
        <p className="pointer-events-none absolute inset-x-0 top-[38%] text-center text-sm text-ink-muted">{emptyLabel}</p>
      ) : null}

      {point && active !== null ? (
        <div
          // The plot runs left to right; its words read in the page's own direction.
          dir={locale === 'ar' ? 'rtl' : 'ltr'}
          className="pointer-events-none absolute top-2 z-10 min-w-36 rounded-control border border-line bg-surface px-3 py-2 text-xs shadow-float"
          style={flip ? { right: width - tooltipLeft + 10 } : { left: tooltipLeft + 10 }}
        >
          <p className="text-ink-muted">{shortDay(point.date, locale)}</p>
          <p data-numeric className="mt-0.5 text-sm font-semibold text-ink">
            {money(point.revenue, locale, currency)}
          </p>
          <p className="mt-0.5 flex items-center gap-1.5 text-ink-muted">
            <span aria-hidden className="inline-block h-0.5 w-3 rounded-full" style={{ backgroundColor: 'var(--chart-1)' }} />
            <span data-numeric>{count(point.orderCount, locale)}</span> {ordersLabel}
          </p>
        </div>
      ) : null}

      {/* The figures themselves, for whoever cannot see the line. */}
      <table id={tableId} className="sr-only">
        <caption>{label}</caption>
        <tbody>
          {points.map((row) => (
            <tr key={row.date}>
              <th scope="row">{shortDay(row.date, locale)}</th>
              <td>{money(row.revenue, locale, currency)}</td>
              <td>
                {count(row.orderCount, locale)} {ordersLabel}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Four or five clean gridlines from zero — 0, 2,000, 4,000 riyals rather than
 * 0, 1,837, 3,674 — and the top they reach, in halalas.
 */
export function niceTicks(max: number): { ticks: number[]; top: number } {
  if (max <= 0) return { ticks: [0], top: 0 };
  const rough = max / 4;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const step = ([1, 2, 2.5, 5, 10].map((m) => m * magnitude).find((candidate) => candidate >= rough) ?? 10 * magnitude);
  const top = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  for (let tick = 0; tick <= top + step / 2; tick += step) ticks.push(Math.round(tick));
  return { ticks, top };
}
